import { inferReadonlyArray } from "@aprao/common/src/array/inferReadonlyArray.js";
import { pull } from "@aprao/common/src/array/pull.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { bigFractionFromNumber } from "@aprao/common/src/math/BigFraction.js";
import { FRACTION_ONE } from "@aprao/common/src/math/fraction.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { wrapFraction } from "@aprao/common/src/math/wrapFraction.js";
import type {
  FundingType,
  PositiveInteger,
  WithName,
  WithRelativesSet,
} from "@aprao/common/src/shapes/common.js";
import { makeAutoObservable } from "mobx";
import { computedFn } from "mobx-utils";
import { mapForNullable } from "option-t/nullable";

import type {
  CostKeys,
  ShapeBuildPhase,
} from "../../shapes/ShapeBuildPhase.js";
import { WithProjectionPeriodsDelegate } from "../delegates/WithProjectionPeriodsDelegate.js";
import type { NamedRelativesSetFacade } from "../NamedRelativesSetFacade.js";
import { namedRelativesSetFacadeWithAbsolute } from "../NamedRelativesSetFacade.js";
import { relativesSetFacadeWithAbsolute } from "../RelativesSetFacade.js";
import type { OperatedAssetGroupFacade } from "../revenue/OperatedAssetGroupFacade.js";
import type { ResidentialSalesGroupFacade } from "../revenue/ResidentialSalesGroupFacade.js";

export interface BuildPhaseLinksBridge {
  isBuildPhaseLinkedToRevenueGroups: (phase: BuildPhaseFacade) => boolean;
  getLinkedMeasurementsByBuildPhase: (phase: BuildPhaseFacade) => {
    totalCount: number;
    totalArea: BigFraction;
  };
}

export type NamedBuildCostFacade = NamedRelativesSetFacade<CostKeys>;

export const createPhaseCost = (
  ordinal: number,
): ShapeBuildPhase["costs"][number] => ({
  name: `Build cost ${ordinal}`,
  relatives: { absolute: null, ratioGrossArea: null, ratioQuantity: null },
});

export class BuildPhaseFacade {
  $model;

  _linkBride;

  $costTotalScale = FRACTION_ONE;

  constructor($model: ShapeBuildPhase, linkBride: BuildPhaseLinksBridge) {
    makeAutoObservable(this);

    this.$model = $model;
    this._linkBride = linkBride;
  }

  get refId() {
    return this.$model.refId;
  }

  get name() {
    return this.$model.name;
  }

  get fundingType() {
    return this.$model.fundingType;
  }

  get grossArea() {
    return relativesSetFacadeWithAbsolute(this.$model.grossArea, {
      ratioNetArea: () => this.netArea,
    });
  }

  get definedNetArea() {
    return wrapFraction(this.$model.definedNetArea);
  }

  get definedSalesItemsQuantity() {
    return this.$model.definedSalesItemsQuantity;
  }

  readonly _decorateCost = computedFn(
    (cost: WithRelativesSet<CostKeys> & WithName) =>
      namedRelativesSetFacadeWithAbsolute(cost, {
        ratioGrossArea: () =>
          this.grossArea.relativesSet.getRelative("absolute"),
        ratioQuantity: () =>
          this.salesItemsCount == null ?
            null
          : bigFractionFromNumber(this.salesItemsCount),
      }),
  );

  get costs() {
    return inferReadonlyArray(
      this.$model.costs.map((cost) => this._decorateCost(cost)),
    );
  }

  get contingency() {
    return relativesSetFacadeWithAbsolute(this.$model.contingency, {
      ratioGrossCost: () => this.costTotal,
    });
  }

  get netArea() {
    return this._linkBride.isBuildPhaseLinkedToRevenueGroups(this) ?
        this._linkBride.getLinkedMeasurementsByBuildPhase(this).totalArea
      : this.definedNetArea;
  }

  get salesItemsCount() {
    return this._linkBride.isBuildPhaseLinkedToRevenueGroups(this) ?
        this._linkBride.getLinkedMeasurementsByBuildPhase(this).totalCount
      : this.definedSalesItemsQuantity;
  }

  get costTotal() {
    return mapForNullable(
      sum(this.costs.map((cost) => cost.relativesSet.getRelative("absolute"))),
      (value) => value.mul(this.$costTotalScale),
    );
  }

  /**
   * Tweaks the value with the given ratio.
   *
   * This is only used to generate the sensitivity analysis and is not
   * persisted into the model.
   */
  rescaleCostTotal(ratio: BigFraction) {
    this.$costTotalScale = ratio;
  }

  get costTotalWithContingency() {
    return sum([
      this.costTotal,
      this.contingency.relativesSet.getRelative("absolute"),
    ]);
  }

  get projectionPeriods() {
    return new WithProjectionPeriodsDelegate(this.$model);
  }

  set name(name: string) {
    this.$model.name = name;
  }

  set fundingType(fundingType: FundingType) {
    this.$model.fundingType = fundingType;
  }

  set definedNetArea(netArea: BigFraction | null) {
    this.$model.definedNetArea = netArea?.toFraction() ?? null;
  }

  set definedSalesItemsQuantity(quantity: PositiveInteger | null) {
    this.$model.definedSalesItemsQuantity = quantity;
  }

  addCost() {
    this.$model.costs.push(createPhaseCost(this.$model.costs.length + 1));
  }

  removeCost(cost: NamedBuildCostFacade) {
    pull(this.$model.costs, cost.$model);
  }

  linkRevenueGroup(
    group: ResidentialSalesGroupFacade | OperatedAssetGroupFacade,
  ) {
    if (
      group.$model.linkedBuildPhaseRefId != null &&
      group.$model.linkedBuildPhaseRefId !== this.$model.refId
    ) {
      throw Error(
        `The given revenue group is already linked to another phase (${group.$model.linkedBuildPhaseRefId})`,
      );
    }

    group.$model.linkedBuildPhaseRefId = this.refId;
  }

  unlinkRevenueGroup(
    group: ResidentialSalesGroupFacade | OperatedAssetGroupFacade,
  ) {
    if (group.$model.linkedBuildPhaseRefId !== this.$model.refId) {
      throw Error(
        "The given revenue group is not currently linked to this phase",
      );
    }

    group.$model.linkedBuildPhaseRefId = null;
  }
}
